<template>
  <section role="main">
    <form
      @submit.prevent="submit"
      class="mt-5 mr-2 ml-2 justify-content-md-center"
    >
      <div class="col">
        <div class="row justify-content-md-center">
          <div class="col-xl-5">
            <section class="card">
              <header class="card-header">
                <h2 class="card-title">Reset Password</h2>
              </header>
              <div class="card-body">
                <div class="form-group">
                  <div class="alert alert-info" role="alert">
                    <p>
                      <i class="fas fa-info-circle mr-1"></i>
                      Please enter your email and new password
                    </p>
                  </div>
                </div>
                <div class="form-group mb-4">
                  <label>Email</label>
                  <input
                    v-model="form.email"
                    type="email"
                    class="form-control"
                    placeholder="Enter your email"
                    required
                    autocomplete="email"
                  />
                </div>
                <div class="form-group mb-4">
                  <label>Password</label>
                  <div class="input-group mb-3">
                    <span class="input-group-prepend">
                      <span
                        class="input-group-text"
                        @click="showPassword = !showPassword"
                      >
                        <i
                          :class="{
                            'far fa-eye': !showPassword,
                            'far fa-eye-slash': showPassword
                          }"
                        ></i>
                      </span>
                    </span>
                    <input
                      v-model="form.password"
                      @input="checkPassword"
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      placeholder="Password"
                      maxlength="50"
                      minlength="8"
                      required
                      autocomplete="new-password"
                    />
                  </div>
                  <ul>
                    <li v-bind:class="{ is_valid: contains_eight_characters }">
                      8 Characters
                    </li>
                    <li v-bind:class="{ is_valid: contains_number }">
                      Contains Number
                    </li>
                    <li v-bind:class="{ is_valid: contains_uppercase }">
                      Contains Uppercase
                    </li>
                    <li v-bind:class="{ is_valid: contains_special_character }">
                      Contains Special Character
                    </li>
                  </ul>
                </div>
                <div class="form-group mb-4">
                  <label>Confirm Password</label>
                  <div class="input-group mb-3">
                    <span class="input-group-prepend">
                      <span
                        class="input-group-text"
                        @click="showConfirmPassword = !showConfirmPassword"
                      >
                        <i
                          :class="{
                            'far fa-eye': !showConfirmPassword,
                            'far fa-eye-slash': showConfirmPassword
                          }"
                        ></i>
                      </span>
                    </span>
                    <input
                      v-model="form.confirmPassword"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      class="form-control"
                      placeholder="Confirm Password"
                      maxlength="50"
                      minlength="8"
                      required
                      autocomplete="new-password"
                    />
                  </div>
                </div>
              </div>
              <div class="card-footer">
                <div class="form-group">
                  <btn-saving :is-saving="isSaving" />
                  <!-- <button type="submit" class="mb-1 mt-1 mr-5 btn btn-primary">Create Account</button> -->
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </form>
  </section>
</template>

<script>
import api from '../../api/http-common.js'
import BtnSaving from '../../components/button-saving.vue'
import { mapActions } from 'vuex'

export default {
  components: {
    BtnSaving
  },
  data() {
    return {
      linkInfo: null,
      isSaving: false,
      form: {
        email: '',
        password: '',
        confirmPassword: ''
      },
      showPassword: false,
      showConfirmPassword: false,
      password_length: 0,
      contains_eight_characters: false,
      contains_number: false,
      contains_uppercase: false,
      contains_special_character: false,
      valid_password: false
    }
  },
  computed: {
    key() {
      return this.$route.query.key
    }
  },
  methods: {
    ...mapActions(['clearUrlLinkInfo']),
    checkPassword() {
      this.password_length = this.form.password.length
      const format = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/

      if (this.password_length > 8) {
        this.contains_eight_characters = true
      } else {
        this.contains_eight_characters = false
      }

      this.contains_number = /\d/.test(this.form.password)
      this.contains_uppercase = /[A-Z]/.test(this.form.password)
      this.contains_special_character = format.test(this.form.password)

      if (
        this.contains_eight_characters === true &&
        this.contains_special_character === true &&
        this.contains_uppercase === true &&
        this.contains_number === true
      ) {
        this.valid_password = true
      } else {
        this.valid_password = false
      }
    },
    async submit() {
      // Validate email
      if (!this.form.email || !this.form.email.trim()) {
        this.$swal({
          position: 'top',
          title: 'Email Required',
          text: 'Please enter your email address to continue with password reset.',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          icon: 'warning'
        })
        return
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.form.email)) {
        this.$swal({
          position: 'top',
          title: 'Invalid Email Format',
          text: 'Please enter a valid email address (e.g., user@example.com).',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          icon: 'warning'
        })
        return
      }

      if (!this.valid_password) {
        const error = 'Please ensure your password meets all requirements:'
        const requirements = []

        if (!this.contains_eight_characters) {
          requirements.push('• At least 8 characters long')
        }
        if (!this.contains_number) {
          requirements.push('• Contains at least 1 number')
        }
        if (!this.contains_uppercase) {
          requirements.push('• Contains at least 1 uppercase letter')
        }
        if (!this.contains_special_character) {
          requirements.push('• Contains at least 1 special character')
        }

        this.$swal({
          position: 'top',
          title: 'Password Requirements Not Met',
          html: `<div style="text-align: left;">${error}<br><br>${requirements.join(
            '<br>'
          )}</div>`,
          showConfirmButton: true,
          confirmButtonText: 'I Understand',
          icon: 'warning'
        })
        return
      }

      if (this.form.password !== this.form.confirmPassword) {
        this.$swal({
          position: 'top',
          title: "Passwords Don't Match",
          text: 'The password and confirm password fields must be identical. Please check and try again.',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          icon: 'warning'
        })
        return
      }

      this.isSaving = true

      try {
        const response = await api.task.request.updateUserPwdFromLink(
          this.linkInfo.taskID,
          this.linkInfo.facilityKey,
          this.key,
          this.form.email,
          this.form.password
        )

        if (!response.data) {
          this.$swal({
            position: 'top',
            title: 'Password Reset Failed',
            text: 'We were unable to reset your password at this time. This could be due to an invalid email or expired reset link. Please request a new password reset.',
            showConfirmButton: true,
            confirmButtonText: 'Request New Reset',
            icon: 'error'
          })
          return
        }

        this.$swal({
          position: 'top',
          title: 'Password Reset Successful!',
          text: 'Your password has been successfully updated. You will now be redirected to the login page.',
          showConfirmButton: false,
          timerProgressBar: true,
          timer: 3000,
          icon: 'success'
        })

        await this.clearUrlLinkInfo()
        await this.$router.push({ path: '/account/login' })
      } catch (ex) {
        console.error('Password reset error:', ex)

        let errorMessage =
          'An unexpected error occurred while resetting your password. '

        if (ex.response && ex.response.status) {
          switch (ex.response.status) {
            case 400:
              errorMessage =
                'The reset link appears to be invalid or has expired. Please request a new password reset link.'
              break
            case 401:
              errorMessage =
                'Your session has expired. Please request a new password reset link.'
              break
            case 404:
              errorMessage =
                'The reset link could not be found. Please request a new password reset link.'
              break
            case 409:
              errorMessage =
                'This email address is not associated with the reset link. Please check your email and try again.'
              break
            case 500:
              errorMessage =
                'Our servers are experiencing issues. Please try again in a few minutes.'
              break
            default:
              errorMessage +=
                'Please try again or contact support if the problem persists.'
          }
        } else if (ex.message) {
          errorMessage += ex.message
        }

        this.$swal({
          position: 'top',
          title: 'Password Reset Failed',
          text: errorMessage,
          showConfirmButton: true,
          confirmButtonText: 'Try Again',
          icon: 'error'
        })
      } finally {
        this.isSaving = false
      }
    }
  },
  async mounted() {
    const resp = await api.auth.request.getLink(this.key)
    this.linkInfo = resp.data
    if (!this.linkInfo) {
      setTimeout(() => {
        this.$router.push({ path: '/account/login' })
      }, 5000)
    }
  }
}
</script>
<style scoped>
ul {
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

li {
  margin-bottom: 8px;
  color: #525f7f;
  position: relative;
}

/* Checkmark & Strikethrough --------- */

.is_valid {
  color: rgba(136, 152, 170, 0.8);
  text-decoration: line-through;
  text-decoration-color: green;
}
.is_valid:before {
  width: 100%;
}

@keyframes draw {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
